import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddServiceForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    duration: '',
    is_trending: false
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category || !formData.price || !formData.duration) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/services`, {
        name: formData.name,
        category: formData.category,
        description: formData.description,
        price: parseFloat(formData.price),
        duration: parseInt(formData.duration),
        is_trending: formData.is_trending
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Service added successfully!');
      setFormData({
        name: '',
        category: '',
        description: '',
        price: '',
        duration: '',
        is_trending: false
      });
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error(error.response?.data?.detail || 'Failed to add service');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        maxWidth: '700px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <h3 style={{ fontSize: '1.25rem', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={20} />
            Add New Service
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} style={{ padding: '1.5rem' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Service Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Women's Haircut"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #f0f0f0',
                borderRadius: '8px'
              }}
            />
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Category *
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
                placeholder="e.g., Haircut"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #f0f0f0',
                  borderRadius: '8px'
                }}
              />
              <p style={{ fontSize: '0.75rem', color: '#5a6c7d', marginTop: '0.25rem' }}>
                Use existing category or create new
              </p>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Mark as Trending
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.75rem',
                border: '2px solid #f0f0f0',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '0.25rem'
              }}>
                <input
                  type="checkbox"
                  checked={formData.is_trending}
                  onChange={(e) => setFormData({ ...formData, is_trending: e.target.checked })}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                  Show trending badge
                </span>
              </label>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Price (₹) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                placeholder="e.g., 1000"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #f0f0f0',
                  borderRadius: '8px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Duration (minutes) *
              </label>
              <input
                type="number"
                min="0"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                required
                placeholder="e.g., 45"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #f0f0f0',
                  borderRadius: '8px'
                }}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              placeholder="Add service description..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #f0f0f0',
                borderRadius: '8px',
                resize: 'vertical'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                flex: 1,
                padding: '0.75rem',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Adding Service...' : 'Add Service'}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: 'white',
                border: '2px solid #f0f0f0',
                borderRadius: '50px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServiceForm;
